// ---------------------------------------------------------------------------
// DeltaIndicator — period-over-period delta for the channel card.
//
//   value === null                  → grey "—" (previous period had no data)
//   |value| > 1.0                   → grey "—" (change too large to compare)
//   |value| < threshold             → grey, no arrow
//   value >= +threshold             → green with ↑
//   value <= -threshold             → red   with ↓
//
// Thresholds:
//   unit='percent' → 0.02  (i.e. 2%)
//   unit='pp'      → 0.005 (i.e. 0.5 percentage points)
//
// The old "+999%↑" / "-99%↓" caps were replaced with the em dash because
// the capped number was visually loud but communicated nothing useful —
// a finite-huge delta typically means the previous window was sparse
// (common in local data, also happens in production when a channel
// bounces back from a zero window). Showing "—" with a tooltip is
// honest: we can't make a meaningful comparison.
// ---------------------------------------------------------------------------

import React from 'react';

export interface DeltaIndicatorProps {
  readonly value: number | null;
  readonly unit?: 'percent' | 'pp';
  /** Optional previous-window raw value — for the capped tooltip. */
  readonly previousValue?: number;
  /** Optional current-window raw value — for the capped tooltip. */
  readonly currentValue?: number;
}

type Variant = 'positive' | 'negative' | 'neutral';

// Coloured text only — no pill background. The delta is annotation beside
// the value, not a standalone badge.
const VARIANT_CLASSES: Readonly<Record<Variant, string>> = {
  positive: 'text-green-400',
  negative: 'text-red-400',
  neutral: 'text-v11-text-muted',
};

const THRESHOLD = { percent: 0.02, pp: 0.005 } as const;

const NO_PREV_TOOLTIP = 'No comparable data in the previous period';
const OUT_OF_RANGE_TOOLTIP =
  'Change is too large for meaningful comparison (previous period was very small)';

export function DeltaIndicator({
  value,
  unit = 'percent',
  previousValue,
  currentValue,
}: DeltaIndicatorProps) {
  // ─── null — no comparable previous period ─────────────────────────────
  if (value === null) {
    return <EmDash title={NO_PREV_TOOLTIP} ariaLabel={NO_PREV_TOOLTIP} />;
  }

  // ─── |value| > 1.0 — change too large to compare meaningfully ─────────
  if (value > 1.0 || value < -1.0) {
    return <EmDash title={OUT_OF_RANGE_TOOLTIP} ariaLabel={OUT_OF_RANGE_TOOLTIP} />;
  }

  // ─── Normal range (including exactly ±1.0) ────────────────────────────
  const threshold = THRESHOLD[unit];
  const variant: Variant =
    Math.abs(value) < threshold ? 'neutral' : value > 0 ? 'positive' : 'negative';

  const formatted = formatValue(value, unit);
  const tooltip =
    previousValue !== undefined && currentValue !== undefined
      ? `Compared to previous period: prev=${previousValue}, curr=${currentValue}`
      : undefined;

  const ariaLabel =
    variant === 'positive'
      ? `increased by ${formatted}`
      : variant === 'negative'
        ? `decreased by ${formatted}`
        : `changed by ${formatted}`;

  return (
    <Pill variant={variant} title={tooltip} ariaLabel={ariaLabel}>
      <span>{formatted}</span>
      {variant === 'positive' && <Arrow direction="up" />}
      {variant === 'negative' && <Arrow direction="down" />}
    </Pill>
  );
}

function EmDash({ title, ariaLabel }: { readonly title: string; readonly ariaLabel: string }) {
  return (
    <span
      data-variant="neutral"
      title={title}
      aria-label={ariaLabel}
      className={`inline-flex items-center text-[10px] font-semibold cursor-help ${VARIANT_CLASSES.neutral}`}
    >
      —
    </span>
  );
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function formatValue(value: number, unit: 'percent' | 'pp'): string {
  if (unit === 'pp') {
    // value is in percentage points already; format to 1 dp with sign.
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}pp`;
  }
  // percent: value is a decimal ratio (0.18 → 18.0%)
  const pct = value * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

function Pill({
  variant,
  title,
  ariaLabel,
  children,
}: {
  readonly variant: Variant;
  readonly title?: string;
  readonly ariaLabel: string;
  readonly children: React.ReactNode;
}) {
  return (
    <span
      data-variant={variant}
      title={title}
      aria-label={ariaLabel}
      className={`inline-flex items-center gap-0.5 text-[10px] font-semibold tabular-nums ${VARIANT_CLASSES[variant]}`}
    >
      {children}
    </span>
  );
}

function Arrow({ direction }: { readonly direction: 'up' | 'down' }) {
  return <span>{direction === 'up' ? '↑' : '↓'}</span>;
}
